import * as React from "react";
import pluginRegistry, {ActionEditProps} from "../pluginRegistry";
import {BaseComponent} from "components/BaseComponent/BaseComponent";
import SpecialVariables from "client/specialVariables";
import {repository} from "clientInstance";
import Roles from "../Roles";
import {ActionSummaryProps} from "../actionSummaryProps";
import {ActionExecutionLocation} from "../../../client/resources/actionExecutionLocation";
import ExpanderSectionHeading from "components/form/Sections/FormSectionHeading";
import FeedResource, {BuiltInFeedId, FeedType} from "client/resources/feedResource";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import ExpandableFormSection from "components/form/Sections/ExpandableFormSection";
import CommonSummaryHelper from "utils/CommonSummaryHelper/CommonSummaryHelper";
import PackageSelector, {PackageSelectorProperties} from "components/PackageSelector/PackageSelector";
import PackageDownloadOptions, {PackageDownloadOptionsProperties} from "components/PackageDownloadOptions/PackageDownloadOptions";
import { VariableLookupText } from "components/form/Text/Text";
import Note from "components/form/Note/Note";
import Summary from "components/form/Sections/Summary";
import routeLinks from "../../../routeLinks";
import {getFeedName} from "../getFeedName";
import { TargetRoles } from "areas/projects/components/DeploymentProcess/ActionDetails";

interface TransferPackageActionSummaryState {
    feedName: string;
}

class AzureCloudServiceActionSummary extends BaseComponent<ActionSummaryProps, TransferPackageActionSummaryState> {
    constructor(props: ActionSummaryProps) {
        super(props);
        this.state = {feedName: null };
    }

    async componentDidMount() {
        this.setState({feedName: await getFeedName(this.getFeedId())});
    }

    render() {
        return <div>
            Transfer package
            <strong> {this.props.properties[SpecialVariables.Action.Package.PackageId]} </strong>
            from {this.state.feedName ? <strong>{this.state.feedName}</strong> : <em>{this.getFeedId()}</em>}
            {this.props.targetRolesAsCSV && <span> to deployment targets in <Roles rolesAsCSV={this.props.targetRolesAsCSV} /> </span>}
        </div>;
    }

    private getFeedId() {
        return this.props.properties[SpecialVariables.Action.Package.FeedId] as string;
    }
}

interface TransferProperties extends PackageSelectorProperties, PackageDownloadOptionsProperties {
    "Octopus.Action.Package.TransferPath": string;
}

interface TransferPackageActionEditState {
    feeds: FeedResource[];
}

export class TransferPackageActionEdit extends BaseComponent<ActionEditProps<TransferProperties>, TransferPackageActionEditState> {
    constructor(props: ActionEditProps<TransferProperties>) {
        super(props);

        this.state = {
            feeds: []
        };
    }

    componentDidMount() {
        this.props.doBusyTask(async () => {
            if (!this.props.properties["Octopus.Action.Package.FeedId"]) {
                this.props.setProperties({ ["Octopus.Action.Package.FeedId"]: BuiltInFeedId }, true);
            }
            this.setState({feeds: await repository.Feeds.all()});
        });
    }

    transferPathSummary() {
        const transferPath = this.props.properties["Octopus.Action.Package.TransferPath"];
        if (!transferPath) {
            return Summary.placeholder("The transfer path has not been configured");
        }
        return Summary.summary(<span>The package will be moved to <strong>{transferPath}</strong> once it is transfered</span>);
    }

    render() {
        const help = this.state.feeds.length > 0
            ? <span>
                This step is used to push the contents of a package to one or more machines which may be sourced from an external feed or the Octopus built-in feed.
                You can configure the remote machines to deploy to in the <InternalLink to={routeLinks.infrastructure.root} openInSelf={false}>Infrastructure</InternalLink> tab.
            </span>
            : <span>Choose the package you which to transfer</span>;

        return <div>
            <ExpanderSectionHeading title="Package Details"/>
            <ExpandableFormSection
                errorKey="Octopus.Action.Package.PackageId|Octopus.Action.Package.FeedId"
                title="Package"
                summary={CommonSummaryHelper.packageSummary(this.props.properties, this.state.feeds)}
                help={help}>
                <PackageSelector
                    setProperties={this.props.setProperties}
                    properties={this.props.properties}
                    projectId={this.props.projectId}
                    feeds={this.state.feeds}
                    feedType={[FeedType.Nuget, FeedType.Maven, FeedType.GitHub]}
                    getFieldError={this.props.getFieldError}
                    localNames={this.props.localNames}
                    errors={this.props.errors} />
                <PackageDownloadOptions
                    setProperties={this.props.setProperties}
                    properties={this.props.properties}
                    projectId={this.props.projectId}
                    localNames={this.props.localNames}
                    errors={this.props.errors} />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="Octopus.Action.Package.TransferPath"
                title="Transfer Path"
                summary={this.transferPathSummary()}
                help="Enter the path the package should be moved to on the remove target.">
                <VariableLookupText
                    localNames={this.props.localNames}
                    projectId={this.props.projectId}
                    value={this.props.properties["Octopus.Action.Package.TransferPath"]}
                    onChange={(x) => this.props.setProperties({["Octopus.Action.Package.TransferPath"]: x})}
                    error={this.props.getFieldError("Octopus.Action.Package.TransferPath")}
                    label="Transfer path" />
                <Note>The location that the package should be moved to once it is uploaded to the remote target.</Note>
            </ExpandableFormSection>
        </div>;
    }
}

pluginRegistry.registerDeploymentAction({
    executionLocation: ActionExecutionLocation.AlwaysOnTarget,
    actionType: "Octopus.TransferPackage",
    summary: (properties, targetRolesAsCSV) => <AzureCloudServiceActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV} />,
    edit: TransferPackageActionEdit,
    canHaveChildren: (step) => true,
    canBeChild: true,
    targetRoleOption: TargetRoles.Optional,
    hasPackages: (action) => true
});


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/Actions/transferPackage/transferPackageAction.tsx